export { default as TressAccordion } from "./TressAccordion.vue";
export { default as TressAccordionItem } from "./TressAccordionItem.vue";
import { createInjectionState } from "@vueuse/core";
import { toRef, ref, computed, type MaybeRefOrGetter, type ComputedRef, type Ref } from "vue";

/**
 * Options that can be passed to the accordion provider.
 * Each option may be a raw value, a `Ref`, or a getter — `MaybeRefOrGetter` makes the
 * provider flexible for consumers.
 */
type AccordionOptions = {
  /** When true, the provider will generate a `groupName` used to scope items for multiple-open behaviour. */
  multiple: MaybeRefOrGetter<boolean>;
  /** When true, items render in a compact/dense style. Optional. */
  dense?: MaybeRefOrGetter<boolean>;
};

type AccordionReturn = {
  groupName: ComputedRef<string | undefined>;
  multiple: Ref<boolean>;
  dense: Ref<boolean>;
};

function useAccordion({ multiple = false, dense = false }: AccordionOptions): AccordionReturn {
  const _groupId = Math.random().toString(36).substring(2, 9);
  const _multiple = toRef(multiple);
  const _dense = toRef(dense);
  const groupName = computed<string | undefined>(() => {
    if (_multiple.value) {
      return `tress-accordion-${_groupId}`;
    }
    return undefined;
  });
  return {
    groupName,
    multiple: _multiple,
    dense: _dense,
  };
}

const [_provideAccordion, _injectAccordion] = createInjectionState(useAccordion);
/**
 * Provider: call inside a parent component's `setup()` to provide accordion state to descendants.
 *
 * Usage (in the provider component):
 * ```ts
 * provideAccordion({ multiple: props.multiple, indented: props.indented, dense: props.dense });
 * ```
 *
 * How it works:
 * 1. `createInjectionState(useAccordion)` creates a provider function that calls `useAccordion` with the
 *    supplied options and `provide`s the returned reactive object to the descendant tree.
 * 2. The provided object contains reactive `Ref`/`ComputedRef` values (`groupName`, `multiple`, `indented`, `dense`).
 * 3. Because `toRef` is used inside `useAccordion`, passing props or refs will keep the provided values reactive
 *    and changes will propagate to any injected consumers.
 *
 * Notes:
 * - Call this in `setup()` of the ancestor wrapper (for example `TressAccordion.vue`).
 * - The provider accepts the same `AccordionOptions` as `useAccordion`.
 */
const provideAccordion = _provideAccordion;
export { provideAccordion };

/**
 * Injector: retrieve the accordion state from the nearest provider.
 *
 * Behavior:
 * 1. A descendant calls `injectAccordion()` to obtain the same reactive state object that the provider created.
 * 2. The returned values are `ref`/`computed` so they remain reactive across the component tree — updates on the
 *    provider side (for example, prop changes) flow to consumers.
 * 3. If no provider exists, this function returns a safe fallback object with reasonable defaults so components
 *    can be used independently or tested in isolation.
 *
 * Returned object shape:
 * - `groupName: ComputedRef<string | undefined>` — defined only when `multiple` is truthy.
 * - `multiple: Ref<boolean>`
 * - `dense: Ref<boolean>`
 */
export function injectAccordion(): AccordionReturn {
  return (
    _injectAccordion() ?? {
      groupName: computed(() => undefined),
      multiple: ref(false),
      dense: ref(false),
    }
  );
}
